/**
 * File name validation and sanitization utility
 * Ensures file names are safe for filesystem operations and prevent security issues
 */
export const sanitizeFileName = (fileName: string): string => {
  // Maximum file name length (255 is common filesystem limit, using 200 for safety)
  const MAX_LENGTH = 200

  // Remove or replace dangerous characters
  let sanitized = fileName
    // Remove path traversal attempts
    .replace(/\.\./g, '')
    .replace(/[\/\\:*?"<>|]/g, '_') // Replace invalid filename characters with underscore
    .replace(/[\x00-\x1F\x7F]/g, '') // Remove control characters
    .replace(/^\s+|\s+$/g, '') // Trim whitespace
    .replace(/\s+/g, ' ') // Normalize multiple spaces to single space

  // Ensure it doesn't start or end with dots or spaces (Windows compatibility)
  sanitized = sanitized.replace(/^[.\s]+|[.\s]+$/g, '')

  // Prevent reserved Windows names
  const reservedNames = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(\.|$)/i
  if (reservedNames.test(sanitized)) {
    sanitized = '_' + sanitized
  }

  // Ensure minimum length
  if (sanitized.length === 0) {
    sanitized = 'Untitled Document'
  }

  // Truncate if too long, preserving file extension if present
  if (sanitized.length > MAX_LENGTH) {
    const lastDotIndex = sanitized.lastIndexOf('.')
    if (lastDotIndex > 0 && lastDotIndex > MAX_LENGTH - 10) {
      // Keep extension if it exists and is reasonable
      const extension = sanitized.substring(lastDotIndex)
      const nameWithoutExt = sanitized.substring(0, lastDotIndex)
      sanitized = nameWithoutExt.substring(0, MAX_LENGTH - extension.length) + extension
    } else {
      sanitized = sanitized.substring(0, MAX_LENGTH)
    }
  }

  return sanitized
}

// Utility function to format file types
export const formatFileType = (type: string): string => {
  switch (type) {
    case 'image/jpeg':
    case 'image/png':
    case 'image/jpg':
    case 'image/gif':
    case 'image/jiff':
    case 'image/bmp':
      return 'Image File'
    case 'application/pdf':
      return 'PDF Document'
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingm':
      return 'Word Document'
    default:
      return type
  }
}
